import { Component, Input, Output, EventEmitter, viewChild, TemplateRef, OnInit, OnDestroy } from '@angular/core';
import { Subject, Observable, BehaviorSubject, Subscription, of, filter, map, switchMap, take, takeUntil } from 'rxjs';
import { animate, state, style, transition, trigger } from '@angular/animations';

import { ControlValueAccessorModel, getProvider } from '../../../../Common/Forms/models/control-value-accessor.model';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { OverlayAnimationStateEnum } from '../../../../Core/Dialogs/enums/overlay-animation.enum';
import { AppearCondition, AutocompleteCustomOption } from '../../../../Common/Forms/components/Autocomplete/autocomplete-custom-option.model';
import { ResourceActionTypes } from '../../../../Common/Basic/enums/resource-actions.enum';

import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { MultiContentLibraryFieldModel } from '../../../../Common/PageBuilder/models/multi-content-library-field-model';
import {
    MultiContentLibraryPanelFieldComponent
} from '../../../../Common/PageBuilder/components/FormFields/MultiContentLibraryPanelField/multi-content-library-panel.component';
import { BriefingImagePropertiesModel } from '../../../../Common/PageBuilder/models/briefing-image-properties.model';
import { MonitoringSourcePickerFieldModel, MonitoringSourcePickerFieldOptions } from '../../../Shared/models/monitoring-source-picker-field.model';
import { DropdownOptionModel } from '../../../../Common/Forms/models/dropdown-option.model';
import { MonitoringSeriesTypeEnum } from '../../../Shared/enums/monitoring-series-type.enum';
import { MentionSearchService } from '../../../Core/services/mention-search.service';
import { monitoringFiltersEnum } from '../../../Shared/enums/monitoring-filters.enum';
import { MonitoringBackEndDataSourceModel } from '../../../Shared/models/monitoring-back-end-data-source.model';
import { BrandIdentityService } from '../../../../Common/BrandIdentity/services/brand-identity.service';
import { BrandIdentity } from '../../../../Common/BrandIdentity/models/brand-identity';
import {
    ManageBrandIdentitiesDialogComponent
} from '../../../../Common/BrandIdentity/components/ManageBrandIdentitiesDialog/manage-brand-identities-dialog.component';
import { MonitoringBrandManagementService } from '../../../Shared/services/monitoring-brand-management.service';
import { MonitoringBrand } from '../../../Shared/models/monitoring-brand.model';
import { QuarterlyDateRangeSettingsModel } from '@Monitor/Shared/models/quarterly-date-range-settings.model';
import { MonthlyDateRangeSettingsModel } from '@Monitor/Shared/models/monthly-date-range-settings.model';

export interface QuarterlyConfigs {
    quarterlyDateRange: boolean;
    quarterlySettings: QuarterlyDateRangeSettingsModel;
    restrictQuarterToMostRecent: boolean;
};

export interface MonthlyConfigs {
    monthlyDateRange: boolean;
    monthlySettings: MonthlyDateRangeSettingsModel;
    restrictMonthToMostRecent: boolean;
};

@Component({
    selector: 'agpr-monitoring-brand-card',
    templateUrl: './monitoring-brand-card.component.html',
    styleUrls: ['./monitoring-brand-card.component.scss'],
    standalone: false,
    providers: [getProvider(MonitoringBrandCardComponent)],
    animations: [
        trigger('cardState', [
            state('collapsed', style({
                height: '0',
            })),
            state('expanded', style({
                height: '*',
            })),
            transition('collapsed <=> expanded', animate('220ms ease-in')),
        ])
    ]
})
export class MonitoringBrandCardComponent extends ControlValueAccessorModel<MonitoringBrand> implements OnInit, OnDestroy {
    readonly buttonTypesEnum = ButtonTypesEnum;
    readonly messageSeverityEnum = messageSeverityEnum;

    readonly logoField = new MultiContentLibraryFieldModel(
        MultiContentLibraryPanelFieldComponent,
        'images',
        'Briefing.onlineBriefing.settings.competitor.logo',
        {
            description: 'PageBuilder.formField.uploadDescription',
            maxFiles: 1,
            placeholder: 'Mention.workbench.image.placeholder',
        },
        true,
        true,
        'Briefing.onlineBriefing.settings.competitor.logo',
        true
    );

    readonly dataSourceFieldOptions: MonitoringSourcePickerFieldOptions = {
        dataSourceLabel: 'Monitoring.sourcePickerField.addCoverageFrom',
        dateRangeLabel: 'Monitoring.sourcePickerField.dateRange',
        mentionCountLocalizationLabel: 'Monitoring.sourcePickerField.briefingsResultFeedbackMessage',
        mentionCountLimitExceededLocalizationTitle: 'Monitoring.bulkActionDialog.sourcePickerField.collectionMayBeReduced',
        mentionCountLimitExceededLocalizationLabel: 'Monitoring.bulkActionDialog.sourcePickerField.pleaseRefineSource',
        warningNumberOfMentions: 400,
        maxNumberOfMentions: 1000,
        dataSourceNullErrorMessage: 'Monitoring.sourcePickerField.dataSourceRequiredMessage',
        dataSourceOptions: [
            new DropdownOptionModel('Monitoring.source.type.folder', MonitoringSeriesTypeEnum.folder),
            new DropdownOptionModel('Monitoring.source.type.topic', MonitoringSeriesTypeEnum.topic),
            new DropdownOptionModel('Monitoring.source.type.subscription', MonitoringSeriesTypeEnum.subscription),
            new DropdownOptionModel('Monitoring.source.type.tag', MonitoringSeriesTypeEnum.tag),
            new DropdownOptionModel('Monitoring.source.type.tagGroup', MonitoringSeriesTypeEnum.tagGroup),
            new DropdownOptionModel('Monitoring.source.type.sourceGroup', MonitoringSeriesTypeEnum.sourceGroup),
            new DropdownOptionModel('Reports.series.type.socialTopic', MonitoringSeriesTypeEnum.socialTopic),
            new DropdownOptionModel('Reports.series.type.theme', MonitoringSeriesTypeEnum.theme),
            new DropdownOptionModel('Reports.series.type.aiAgent', MonitoringSeriesTypeEnum.aiAgent)
        ],
        showTooManyMentionsError: true,
        showFilters: true,
        orderedMercuryFilters: MentionSearchService.defaultOrderedMercuryFilters.filter((item) => {
            // @TODO: This list should vary depending on the selected source, different options are available for saved vs unsaved mentions
            // likely needs a refactor
            return item !== monitoringFiltersEnum.domainAuthority;
        }),
        separatedErrors: true,
        showResultsFeedback: true,
        showResultsFeedbackAnalysis: false,
        showLoadingFeedback: true
    };

    readonly brandAutocompleteCustomOptions: AutocompleteCustomOption[] = [
        {
            label: 'Common.dialog.aeoScout.insights.brandIdentity.autocomplete.createBrand',
            action: (name: string) => {
                const brand = new BrandIdentity();
                brand.name = name;

                const dialogClosed$ = this.openManageBrandsDialog(brand);

                let brandId: number | null;
                this.brandIdentityService.actionCompleted$.pipe(
                    filter(action => action.actionType === ResourceActionTypes.CREATE && !action.error),
                    take(1),
                    takeUntil(dialogClosed$)
                ).subscribe(action => {
                    brandId = action.resource?.id ?? null;
                });

                dialogClosed$.pipe(
                    take(1),
                    filter(() => !!brandId || brandId === 0),
                    switchMap(() => this.brandIdentityService.all$.pipe(
                        filter(brands => !!brands),
                        map(brands => brands.find(b => b.id === brandId)),
                        filter(x => !!x),
                        take(1)
                    ))
                ).subscribe(newBrand => {
                    this.selectBrand(newBrand);
                });
            },
            appearCondition: AppearCondition.noExactMatchInSuggestions,
            clearOnSelect: true
        }
    ];

    readonly dataSourcePicker = viewChild<TemplateRef<void>>('dataSourcePicker');

    @Input() id: string = 'agpr_monitoring_brand_card';
    @Input() set dataSourceConfigs(value: MonitoringSourcePickerFieldOptions) {
        this.dataSourceField = new MonitoringSourcePickerFieldModel(
            null as any,
            'backendDataSource',
            '',
            '',
            {
                ...this.dataSourceFieldOptions,
                ...value

            }
        );
    }

    @Input() monthlyConfigs: MonthlyConfigs = {
        monthlyDateRange: false,
        monthlySettings: new MonthlyDateRangeSettingsModel(),
        restrictMonthToMostRecent: false
    };

    @Input() quarterlyConfigs: QuarterlyConfigs = {
        quarterlyDateRange: false,
        quarterlySettings: new QuarterlyDateRangeSettingsModel(),
        restrictQuarterToMostRecent: false
    };

    @Input() showLogoField: boolean = false;
    @Input() showDataSourceField: boolean = false;
    @Input() hideBorder: boolean = false;
    @Input() toggleButtonLabel: string = '';
    @Input() isGlobalDateRange: boolean = false;
    @Input() showRecentColors: boolean = false;

    @Output() brandSelected = new EventEmitter<BrandIdentity | null>();

    subscriptions: Subscription[] = [];

    cardState: boolean = true;

    brand: BrandIdentity | null = null;
    brandList$: Observable<BrandIdentity[]>;
    brandList: BrandIdentity[] = [];

    name: string = '';
    placeholderText: string = '';
    hasNameError: boolean = false;
    isEditingName: boolean = false;
    nameInputValue: string = '';

    logo: BriefingImagePropertiesModel[] = [];

    defaultColor: string = '#0D74BA';
    color: string = '';

    dataSourceField: MonitoringSourcePickerFieldModel = new MonitoringSourcePickerFieldModel(
        null as any,
        'backendDataSource',
        '',
        '',
        this.dataSourceFieldOptions
    );
    defaultDataSourceType: MonitoringSeriesTypeEnum = MonitoringSeriesTypeEnum.folder;
    dataSource: MonitoringBackEndDataSourceModel = new MonitoringBackEndDataSourceModel(MonitoringSeriesTypeEnum.folder);
    hasDataSourceError: boolean = false;

    private brandDataSource: MonitoringBackEndDataSourceModel | null = null; // the default data source populated from the selected brand
    private brandLogo: string = ''; // the default logo populated from the selected brand
    private brandColor: string = ''; // the default color populated from the selected brand

    constructor(
        private coreDialogService: CoreDialogService,
        private brandIdentityService: BrandIdentityService,
        private monitoringBrandManagementService: MonitoringBrandManagementService,
    ) {
        super();
        this.brandList$ = this.brandIdentityService.all$;
    }

    ngOnInit(): void {
        this.subscriptions.push(
            this.brandIdentityService.all$.subscribe(brands => this.brandList = brands)
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    private getPlaceholderText(name: string): string {
        let res = name?.trim() ?? '';
        if (!res.length) {
            return res;
        }

        const nameSplit = name.split(' ');
        if (nameSplit.length === 1) {
            res = name.slice(0, Math.min(nameSplit[0].length, 2)).toLocaleUpperCase();
        } else {
            // use the first char of the first and last word
            res = nameSplit[0][0].toLocaleUpperCase() + nameSplit[nameSplit.length - 1][0].toLocaleUpperCase();
        }

        return res;
    }

    selectBrand(brand: BrandIdentity) {
        if (!brand) {
            this.brand = null;
            this.emitValue();
            return;
        }
        this.brand = brand.clone();

        this.name = this.brand.name?.trim() ?? '';
        this.nameChanged();

        // If there is no customized logo
        const hasCustomLogo = this.logo?.[0]?.url && this.logo?.[0]?.url !== this.brandLogo;
        if (!hasCustomLogo) {
            const brandLogo = this.brand?.logo?.source ?? '';
            if (brandLogo !== this.brandLogo) {
                this.brandLogo = brandLogo;
                if (brandLogo) {
                    const logo = new BriefingImagePropertiesModel();
                    logo.url = brandLogo;
                    this.logoChanged([logo]);
                } else {
                    // clear the field
                    this.logoChanged([]);
                }
            }
        }

        // If there is no customized brand color
        const hasCustomColor = this.color !== this.defaultColor && this.color !== this.brandColor;
        if (!hasCustomColor) {
            const colors: string[] = this.brand.colors?.sort((a, b) => Number(b.favorite) - Number(a.favorite))?.map(x => x.color) ?? [];
            this.brandColor = colors?.[0] ?? '';
            this.colorChanged(colors?.[0]);
        }

        // If there is no customized data source, populate the favorite topic of the selected brand.
        // i.e., the selected data source has no filters applied and it's the same as the previously selected brand's topic
        const hasCustomDataSource = this.dataSource?.id
            && (this.dataSource?.type !== this.brandDataSource?.type
                || this.dataSource?.id !== this.brandDataSource?.id
                || this.dataSource?.combinedSearchCriteria?.filterQuery);

        const request$: Observable<MonitoringBackEndDataSourceModel | null> = hasCustomDataSource
            ? of(null)
            : this.monitoringBrandManagementService.getBrandDataSource(this.brand);
        request$.subscribe(dataSource => {
            if (dataSource) {
                this.brandDataSource = dataSource.id ? dataSource.clone() : null;
                this.dataSourceChanged(dataSource);
            }
            this.brandSelected.emit(this.brand);
            this.emitValue();
        });
    }

    openManageBrandsDialog(brand?: BrandIdentity): Observable<void> {
        const currentBrand = brand ?? this.brandList.find(item => item.id === this.brand?.id);
        if (currentBrand) {
            this.brandIdentityService.setCurrentResource(currentBrand);
        }
        const dialogClosed$ = new Subject<void>();
        this.coreDialogService.open(ManageBrandIdentitiesDialogComponent, {
            inputs: {
                overlayContainer: document.getElementById('main-content'),
                animationTrigger: new BehaviorSubject(
                    new FullProfileAnimationModel(
                        FullProfileDialogAnimationsEnum.slideInOutAnimation,
                        OverlayAnimationStateEnum.open
                    )
                )
            },
            outputs: {
                dialogClose: () => {
                    dialogClosed$.next();
                    this.brandIdentityService.setCurrentResource(new BrandIdentity());
                }
            }
        });

        return dialogClosed$.asObservable();
    }

    doneEditingName() {
        if (!this.nameInputValue?.trim() || this.nameInputValue?.trim() === this.name) {
            this.isEditingName = false;
            return;
        }
        this.name = this.nameInputValue.trim();
        this.nameChanged();
    }

    nameChanged() {
        this.hasNameError = false;
        this.placeholderText = this.getPlaceholderText(this.name);
        this.isEditingName = false;
        this.emitValue();
    }

    /**
     * Used for calculating `displaySize` in canvas template.
     * See in briefings-template-factory.service.ts:
     *   * getMediaCoverageBriefingTemplate
     *   * getCompetitorTemplate
     *  */
    getImageDimensions(event: Event): void {
        const img = event.target as HTMLImageElement;
        this.logo[0].naturalWidth = img.naturalWidth;
        this.logo[0].naturalHeight = img.naturalHeight;
    }

    logoChanged(logo: BriefingImagePropertiesModel[]) {
        this.logo = logo;
        this.emitValue();
    }


    colorChanged(color: string) {
        this.color = color || this.defaultColor;
        this.emitValue();
    }

    dataSourceChanged(dataSource: MonitoringBackEndDataSourceModel): void {
        this.dataSource = dataSource;
        this.hasDataSourceError = false;
        this.dataSourceField.showDataSourceError = false;
        this.emitValue();
    }

    validate(): boolean {
        const isNameValid = this.name?.trim().length > 0;
        this.hasNameError = !isNameValid;

        const isDataSourceValid = this.dataSource?.id || this.dataSource?.id === 0;
        this.hasDataSourceError = !isDataSourceValid;
        this.dataSourceField.showDataSourceError = !isDataSourceValid;

        return isNameValid && !!isDataSourceValid;
    }

    emitValue() {
        this.value.brand = this.brand;
        this.value.name = this.name;
        this.value.logo = this.logo;
        this.value.color = this.color;
        this.value.dataSource = this.dataSource;
        this.onModelChange(this.value);
        this.onModelTouched();
    }

    override writeValue(value: MonitoringBrand): void {
        this.value = value ??  new MonitoringBrand();
        this.brand = this.value.brand;
        this.name = this.value.name;
        this.placeholderText = this.getPlaceholderText(this.name);
        this.hasNameError = false;
        this.logo = this.value.logo || [];
        if (!this.showLogoField && this.value.brand?.logo) {
            const logo = new BriefingImagePropertiesModel();
            logo.url = this.value.brand.logo.source ?? '';
            this.logo = [logo];
        }
        this.brandLogo = this.brandLogo = this.value.logo?.[0]?.url ?? '';
        this.color = this.value.color || this.defaultColor;
        this.defaultColor = this.color;
        this.brandColor = this.color;
        this.dataSource = this.value.dataSource;
        this.brandDataSource = this.value.dataSource?.clone() ?? null;
        this.hasDataSourceError = false;
        this.dataSourceField.showDataSourceError = false;
        // clear the cached data source, logo and color
    }
}
